import CircularProgressbarNewWithChildren from 'components/CircularProgressbar/CircularProgressbarSmallWithChildren';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { CheckListStatus } from 'styledComponents/tasks';

import officeIcon from '../../../assets/img/Sidebar/offices-icon.svg';
import calendarIcon from '../../../assets/img/icons/Calendar.svg';
import documentIcon from '../../../assets/img/icons/Document.svg';
import assigneeIcon from '../../../assets/img/icons/assignee.svg';
import tick from '../../../assets/img/icons/tick-white-bold.svg';
import statusIcon from '../../../assets/img/statusIcon.svg';
import { CardText } from '../../../components/DashboardCards/cardStyles';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../styledComponents/buttons';
import {
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonText,
  Container,
  FirstWordRound,
  InputLabel,
} from '../../../styledComponents/common';
import {
  CardBlock,
  GreenCircle,
  InfoSectionCard,
  LateTag,
} from '../../FieldServices/RouteMapNew/routeMapStyles';
import AudioPlayer from '../TaskApproval/AudioPlayer';
import EvidenceModal from '../TaskApproval/EvidenceModal';
import ApprovalSection from '../TaskDetails/approvalSection';
import Comment from '../TaskDetails/comment';
import {
  CallSection,
  EmployeeInfoBlock,
  EvidenceThumbnail,
  FullWidthWithTopBorder,
  LabelTextBlock,
  NoteSection,
  StatusBar,
  TaskDetailsInfoSection,
  TaskInfoSection,
} from '../TaskDetails/taskDetailsStyles';
import TaskEvidence from '../TaskDetails/taskEvidence';

const SubTaskDetails = ({
  subTaskDetails,
  approvalMode,
  selectedTab,
  updateSubTaskStatusRequest,
  getSubTaskEvidence,
}) => {
  const { t } = useTranslation();
  const [supervisorNote, setSupervisorNote] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleApproveReject = status => {
    updateSubTaskStatusRequest(subTaskDetails.id, status);
  };

  const toggleImageModal = image => {
    setSelectedImage(image);
    setShowImageModal(!showImageModal);
  };

  // Status colors mapping
  const getStatusColor = status => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'danger';
    if (status === 'pending') return 'warning';
    return 'secondary';
  };

  return (
    <Container padding="30px" gap="20px">
      <CommonText name title fontSize="16px">
        {subTaskDetails.name}
      </CommonText>

      <CommonText fontSize="14px" className="mt-2">
        <strong>Task Code:</strong> {subTaskDetails.task_code}
      </CommonText>

      <TaskDetailsInfoSection>
        <TaskDetailsSingleInfo
          icon={statusIcon}
          title="status"
          value={
            <CommonFlex>
              <CheckListStatus
                status={getStatusColor(subTaskDetails.approval_status)}
              >
                {subTaskDetails.approval_status.toUpperCase()}
              </CheckListStatus>
              <CheckListStatus
                status={
                  subTaskDetails.status.includes('completed')
                    ? 'completed'
                    : subTaskDetails.status
                }
              >
                {subTaskDetails.status.toUpperCase()}
              </CheckListStatus>
            </CommonFlex>
          }
        />{' '}
        <TaskDetailsSingleInfo
          icon={officeIcon}
          title="public_zone"
          value={<CommonText>{subTaskDetails?.public_zone_name}</CommonText>}
        />
        <TaskDetailsSingleInfo
          icon={officeIcon}
          title="office"
          value={<CommonText>{subTaskDetails?.office_name}</CommonText>}
        />
        <TaskDetailsSingleInfo
          icon={assigneeIcon}
          title="assignee"
          value={
            subTaskDetails?.assignees?.length > 0 ? (
              <CommonFlex>
                <FirstWordRound
                  size="30px"
                  fontSize="11px"
                  backColor="#789CFF"
                >{`${subTaskDetails?.assignees[0].first_name
                  .toUpperCase()
                  .slice(0, 2)}`}</FirstWordRound>
                <CommonText>
                  {subTaskDetails?.assignees[0].first_name +
                    ' ' +
                    subTaskDetails?.assignees[0].last_name}
                </CommonText>
              </CommonFlex>
            ) : (
              <></>
            )
          }
        />
        <TaskDetailsSingleInfo
          icon={documentIcon}
          title="task_code"
          value={<CommonText>{subTaskDetails.task_code}</CommonText>}
        />
        <TaskDetailsSingleInfo
          icon={documentIcon}
          title="type"
          value={<CommonText>{subTaskDetails.sub_task_type}</CommonText>}
        />
        <TaskDetailsSingleInfo
          icon={documentIcon}
          title="item"
          value={<CommonText>{subTaskDetails.item_name}</CommonText>}
        />
        <TaskDetailsSingleInfo
          icon={documentIcon}
          title="quantity"
          value={<CommonText>{subTaskDetails.quantity}</CommonText>}
        />
        <TaskDetailsSingleInfo
          icon={documentIcon}
          title="current_inventory"
          value={<CommonText>{subTaskDetails.current_inventory}</CommonText>}
        />
        {subTaskDetails.reason && (
          <TaskDetailsSingleInfo
            icon={documentIcon}
            title="reason"
            value={<CommonText>{subTaskDetails.reason}</CommonText>}
          />
        )}
      </TaskDetailsInfoSection>

      <InfoSectionCard style={{ maxWidth: `530px` }}>
        <CardBlock>
          <CardText name fontSize="13px" title nowrap>
            {subTaskDetails.created_at
              ? moment(subTaskDetails.created_at).format('DD/MM/YYYY HH:mm')
              : '-'}
          </CardText>
          <CardText fontSize="12px" label title>
            {t('created_at')}
          </CardText>
        </CardBlock>

        {subTaskDetails.completed_at && (
          <CardBlock>
            <CardText name fontSize="13px" title nowrap>
              {moment(subTaskDetails.completed_at).format('DD/MM/YYYY HH:mm')}
            </CardText>
            <CardText fontSize="12px" label title>
              {t('completed_at')}
            </CardText>
          </CardBlock>
        )}
      </InfoSectionCard>

      <FullWidthWithTopBorder>
        <CommonText name title fontSize="16px">
          {t('comments')}
        </CommonText>
        <Comment type="employee_note" comment={subTaskDetails.employee_note} />
        <Comment
          type="supervisor_note"
          comment={subTaskDetails.supervisor_note}
        />
      </FullWidthWithTopBorder>

      <FullWidthWithTopBorder>
        <TaskEvidence evidences={subTaskDetails?.evidences} />
      </FullWidthWithTopBorder>

      {(subTaskDetails?.status.includes('completed') ||
        subTaskDetails?.status === 'not completed') && ////////////////
        subTaskDetails?.approval_status === 'pending' &&
        localStorage.getItem('user_role') !== 'employee' &&
        selectedTab === 'submitted' &&
        subTaskDetails?.assignees &&
        subTaskDetails?.assignees.length > 0 &&
        subTaskDetails?.assignees[0].id !== localStorage.getItem('user_id') && (
          <FullWidthWithTopBorder max={'auto'}>
            <CommonGrid columns={'3fr 1fr'}>
              <ApprovalSection
                task_id={subTaskDetails?.id}
                history={history}
                updateTaskStatusRequest={handleApproveReject}
                percentage={100}
              />
            </CommonGrid>
          </FullWidthWithTopBorder>
        )}

      <EvidenceModal
        isOpen={showImageModal}
        toggle={toggleImageModal}
        evidence={selectedImage}
        isLoading={false}
      />
    </Container>
  );
};

export default SubTaskDetails;

const TaskDetailsSingleInfo = ({ icon, title, value }) => {
  const { t } = useTranslation();
  return (
    <CommonGrid columns="200px 1fr">
      <CommonGrid columns="15px 1fr" alignItem="center">
        <img src={icon} alt="" style={{ height: '16px', width: '16px' }} />
        <CommonText> {t(title)}</CommonText>
      </CommonGrid>
      <div>{value}</div>
    </CommonGrid>
  );
};
